import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import {
  Copy,
  Heart,
  Headphones,
  Instagram,
  MessageCircle,
  Minus,
  Plus,
  RefreshCw,
  ShieldCheck,
  Star,
  Truck,
} from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";
import { FoodSymbol } from "@/components/FoodSymbol";
import RecentlyViewedProducts from "@/components/RecentlyViewedProducts";
import { HamperBuilder } from "@/components/HamperBuilder";
import { recordRecentlyViewedProduct } from "@/lib/recentlyViewed";
import { SeoHead } from "@/components/SeoHead";
import { ProductExperienceLuxury } from "@/components/ProductExperienceLuxury";
import { absoluteSeoUrl, buildProductSeo, productSeoPath } from "@/lib/seo";

const Tab = ({ active, children, onClick }) => (
  <button onClick={onClick} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${active ? "border-[#2D5A47] text-[#2D5A47]" : "border-transparent text-[#64748B] hover:text-[#1E2A24]"}`}>
    {children}
  </button>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [img, setImg] = useState(0);
  const [variant, setVariant] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("desc");
  const [reviews, setReviews] = useState([]);
  const [customersAlsoBought, setCustomersAlsoBought] = useState([]);
  const [bogoCoupons, setBogoCoupons] = useState([]);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyRequested, setNotifyRequested] = useState(false);
  const { add, items, setQty: setCartQuantity } = useCart();
  const { user, wishlistIds, toggleWishlist } = useAuth();
  const nav = useNavigate();
  const selectedCartItem = p && variant
    ? items.find((item) => item._k === `${p.id}::${variant.sku || `${variant.label}::${variant.material || ""}`}`)
    : null;
  const bogoOffer = bogoCoupons.find((coupon) => coupon.product_ids?.includes(p?.id));
  const productSeo = useMemo(() => {
    return p ? buildProductSeo(p, reviews) : null;
  }, [p, reviews]);
  const missingProductSeo = useMemo(() => ({
    title: "Product Not Found | Eira Products",
    description: "The requested Eira Products item could not be found.",
    canonical: absoluteSeoUrl(productSeoPath({ slug })),
    noIndex: true,
    type: "website",
  }), [slug]);

  const subscribeNotifyMe = async () => {
    const email = notifyEmail || user?.email;
    if (!email) {
      toast.error("Enter your email to be notified.");
      return;
    }
    try {
      await api.post(`/products/${p.id}/notify-me`, { email, variant_label: variant?.label || null });
      setNotifyRequested(true);
      localStorage.setItem(`notify-me-email:${p.id}:${variant?.label || "product"}`, email);
      toast.success("We’ll email you when this item is back in stock.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save your notification request.");
    }
  };

  const currentSelectedStock = variant?.stock ?? p?.stock ?? 0;
  const productId = p?.id;
  const selectedVariantLabel = variant?.label;

  useEffect(() => {
    if (!productId || currentSelectedStock > 0) {
      setNotifyRequested(false);
      return;
    }
    const savedEmail = localStorage.getItem(
      `notify-me-email:${productId}:${selectedVariantLabel || "product"}`,
    );
    const email = user?.email || savedEmail;
    if (!email) {
      setNotifyRequested(false);
      return;
    }
    api.get(`/products/${productId}/notify-me/status`, {
      params: { email, variant_label: selectedVariantLabel || undefined },
    })
      .then((response) => setNotifyRequested(response.data.requested))
      .catch(() => setNotifyRequested(false));
  }, [currentSelectedStock, productId, selectedVariantLabel, user?.email]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setP(null);
    setNotFound(false);
    setImg(0);
    setQty(1);
    setTab("desc");
    setCustomersAlsoBought([]);
    api.get(`/products/${slug}`)
      .then((response) => {
        setP(response.data);
        recordRecentlyViewedProduct(response.data);
        setImg(response.data.video_url ? "video" : 0);
        const variants = response.data.variants || [];
        const onlyVariant = variants.length === 1 ? variants[0] : null;
        setVariant(onlyVariant);
        setSelectedMaterial(onlyVariant?.material || "");
        api.get(`/reviews/${response.data.id}`).then((reviewResponse) => {
          setReviews(reviewResponse.data);
        });
        api.get(`/products/${response.data.id}/customers-also-bought`)
          .then((response) => setCustomersAlsoBought(response.data.items || []))
          .catch(() => setCustomersAlsoBought([]));
      })
      .catch(() => setNotFound(true));
    api.get("/coupons").then((response) => {
      setBogoCoupons(response.data.filter((coupon) => coupon.kind === "bogo"));
    });
  }, [slug]);

  useEffect(() => {
    if (!p || !variant) {
      return;
    }
    if (selectedCartItem) {
      setQty(selectedCartItem.quantity);
      return;
    }
    setQty(1);
  }, [p, variant, selectedCartItem]);

  if (!p) {
    return (
      <>
        {notFound && <SeoHead seo={missingProductSeo} />}
        <div className="mx-auto max-w-7xl px-4 py-20 text-center">
          {notFound ? "Product not found." : "Loading…"}
        </div>
      </>
    );
  }

  const isHamper = p.product_type === "gift_box";
  const price = variant?.price ?? p.price;
  const mrp = variant?.mrp ?? p.mrp;
  const hasVariants = (p.variants || []).length > 0;
  const selectedStock = variant?.stock ?? p.stock;
  const off = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const returnLabel = p.return_policy && p.return_policy !== "no_return"
    ? `${p.return_policy.replace("_", " ").replace("day", "Day")} Returns`
    : "No Returns";
  const reviewCount = reviews.length;
  const averageRating = reviewCount
    ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviewCount
    : null;
  const formatReviewDate = (value) => {
    if (!value) return "";
    return new Intl.DateTimeFormat("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  };
  const ensureSelectedVariant = () => {
    if (p.variants?.length > 1 && !variant) {
      toast.error("Please select a variant before adding this product to your bag.");
      return false;
    }
    if (selectedStock <= 0) {
      toast.error("This variant is out of stock");
      return false;
    }
    if (qty <= 0) {
      toast.error("Use + to choose a quantity before adding to cart");
      return false;
    }
    if (qty > selectedStock) {
      toast.error(`Only ${selectedStock} unit(s) available.`);
      return false;
    }
    return true;
  };
  const selectVariant = (nextVariant) => {
    const nextCartItem = items.find(
      (item) => item._k === `${p.id}::${nextVariant.sku || `${nextVariant.label}::${nextVariant.material || ""}`}`,
    );
    setVariant(nextVariant);
    setQty(nextCartItem?.quantity || 1);
  };
  const materials = [...new Set((p.variants || []).map((item) => item.material || "Standard"))];
  const materialVariants = selectedMaterial
    ? (p.variants || []).filter((item) => (item.material || "Standard") === selectedMaterial)
    : [];
  const sizes = [...new Set(materialVariants.map((item) => item.label))];
  const selectMaterial = (material) => {
    const currentSize = variant?.label;
    const nextMaterialVariants = (p.variants || []).filter(
      (item) => (item.material || "Standard") === material,
    );
    const matchingSizeVariant = nextMaterialVariants.find(
      (item) => item.label === currentSize,
    );
    const nextVariant = matchingSizeVariant || nextMaterialVariants[0] || null;
    setSelectedMaterial(material);
    if (nextVariant) {
      selectVariant(nextVariant);
      return;
    }
    setVariant(null);
    setQty(1);
  };
  const selectSize = (size) => {
    const matchedVariant = materialVariants.find((item) => item.label === size);
    if (matchedVariant) selectVariant(matchedVariant);
  };
  const changeQuantity = (nextQuantity) => {
    const safeQuantity = Math.min(
      Math.max(1, nextQuantity),
      Math.max(1, Number(selectedStock || 0)),
    );
    setQty(safeQuantity);
    if (selectedCartItem) {
      setCartQuantity(selectedCartItem._k, safeQuantity);
    }
  };
  const addSelectedVariant = () => {
    if (!ensureSelectedVariant()) return false;
    const quantityToAdd = selectedCartItem ? 1 : Math.max(qty, 1);
    if (!add(p, variant, quantityToAdd)) return false;
    toast.success("Item added to cart", { duration: 2500 });
    return true;
  };
  const wishlisted = wishlistIds.includes(p?.id);
  const handleWishlist = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    try {
      const action = await toggleWishlist(p.id);
      toast.success(action === "added" ? "Added to wishlist" : "Removed from wishlist");
    } catch {
      toast.error("Could not update wishlist");
    }
  };
  const productShareUrl = `${window.location.origin}/product/${p.slug || slug}`;
  const copyProductLink = async (message) => {
    try {
      await navigator.clipboard.writeText(productShareUrl);
      toast.success(message);
    } catch {
      toast.error("Could not copy the product link");
    }
  };
  const shareOnWhatsApp = () => {
    const shareText = `${p.name} — ${productShareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <SeoHead seo={productSeo} />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8" data-testid="pdp-page">
      <div className="text-xs text-[#64748B] mb-6">
        <Link to="/">Home</Link> / <Link to="/shop">Shop</Link> / <span className="text-[#1E2A24]">{p.name}</span>
      </div>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-[#F4F1EA] mb-4">
            {img === "video" && p.video_url ? (
              <video src={p.video_url} controls autoPlay muted playsInline className="w-full h-full object-cover" />
            ) : (
              <img
                src={p.images?.[img] || p.images?.[0]}
                alt={p.name}
                decoding="async"
                fetchPriority="high"
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {p.images?.slice(0, 4).map((im, i) => (
              <button key={i} onClick={() => setImg(i)} className={`aspect-square rounded-lg overflow-hidden bg-[#F4F1EA] border-2 ${i === img ? "border-[#2D5A47]" : "border-transparent"}`}>
                <img
                  src={im}
                  alt={`${p.name} thumbnail ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
            {p.video_url && (
              <button onClick={() => setImg("video")} className={`relative aspect-square rounded-lg overflow-hidden bg-black border-2 ${img === "video" ? "border-[#C86D42]" : "border-transparent"}`} data-testid="pdp-video-thumb">
                <video src={p.video_url} muted className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="w-8 h-8 rounded-full bg-white/90 flex items-center justify-center">
                    <svg width="10" height="12" viewBox="0 0 10 12" fill="#2D5A47"><path d="M0 0v12l10-6z" /></svg>
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#8C6D46] mb-2">{p.brand}</div>
          <div className="flex items-start gap-2">
            <FoodSymbol
              value={p.food_symbol}
              className="mt-2 shrink-0"
              testId={`product-detail-food-symbol-${p.id}`}
            />
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1E2A24]">{p.name}</h1>
          </div>
          {bogoOffer && (
            <div
              className="mt-4 border-l-4 border-[#2D5A47] bg-[#EBF2EE] px-4 py-3 text-sm text-[#1E2A24]"
              data-testid="product-detail-bogo-offer"
            >
              <p className="font-semibold">Buy 1 Get 1 Free on this product</p>
              <p className="mt-1 text-[#64748B]">
                Add any two eligible items to your cart. The lower-priced item is free; if both are
                the same product, one unit is free. Use code {bogoOffer.code} at checkout.
              </p>
            </div>
          )}
          {p.color_code && <div className="mt-2 flex gap-2 text-xs"><span className="inline-flex items-center gap-1"><i className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color_code }} />{p.color_code}</span></div>}
          {p.return_policy && p.return_policy !== "no_return" && <div className="mt-2 text-xs font-medium text-[#2D5A47]" data-testid="product-return-policy">{p.return_policy.replace("_", " ").replace("day", "Day").replace(/(^\w)/, (letter) => letter.toUpperCase())} Return</div>}
          {reviewCount > 0 && <div className="flex items-center gap-3 mt-3">
            <div className="flex items-center gap-1 text-[#D99B26]">
              {[1, 2, 3, 4, 5].map((s) => <Star key={s} size={16} fill={s <= Math.round(averageRating) ? "#D99B26" : "none"} strokeWidth={0} />)}
            </div>
            <span className="text-sm text-[#64748B]">{averageRating.toFixed(1)} ({reviewCount} reviews)</span>
          </div>
          }
          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl font-bold text-[#2D5A47]">{isHamper && <span className="mr-2 text-sm font-normal">From</span>}{inr(price)}</span>
            {off > 0 && (
              <>
                <span className="text-lg text-gray-400 line-through">{inr(mrp)}</span>
                <span className="bg-[#C86D42] text-white text-xs font-bold px-2 py-1 rounded">-{off}%</span>
              </>
            )}
          </div>
          <div className="text-xs text-[#64748B] mt-1">Inclusive of all taxes</div>

          {isHamper ? <HamperBuilder key={p.id} product={p} /> : <>
          {hasVariants && (
            <div className="mt-6">
              <div className="text-sm font-semibold mb-2">Select Material</div>
              <div className="flex flex-wrap gap-2">
                {materials.map((material) => (
                  <button
                    key={material}
                    type="button"
                    onClick={() => selectMaterial(material)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium ${
                      selectedMaterial === material
                        ? "bg-[#2D5A47] text-white border-[#2D5A47]"
                        : "bg-white border-[#E2DCD2] hover:border-[#2D5A47]"
                    }`}
                    data-testid={`variant-material-${material}`}
                  >
                    {material}
                  </button>
                ))}
              </div>
              <div className="mt-5 text-sm font-semibold mb-2">Select Size</div>
              <div className="flex flex-wrap gap-2">
                {sizes.map((size) => {
                  const sizeVariant = materialVariants.find((item) => item.label === size);
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => selectSize(size)}
                      disabled={(sizeVariant?.stock ?? p.stock) <= 0}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium ${
                        variant?.sku === sizeVariant?.sku
                          ? "bg-[#2D5A47] text-white border-[#2D5A47]"
                          : "bg-white border-[#E2DCD2] hover:border-[#2D5A47]"
                      } disabled:cursor-not-allowed disabled:opacity-40`}
                      data-testid={`variant-size-${selectedMaterial}-${size}`}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
              {!variant && p.variants.length > 1 && (
                <p
                  className="mt-2 text-xs font-medium text-[#C86D42]"
                  data-testid="variant-selection-required"
                >
                  Please select material first, then choose a size.
                </p>
              )}
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-[#E2DCD2] rounded-lg bg-white">
              <button
                type="button"
                onClick={() => changeQuantity(qty - 1)}
                className="p-3"
                data-testid="quantity-decrease"
              >
                <Minus size={14} />
              </button>
              <span className="px-4 font-medium" data-testid="quantity-value">{qty}</span>
              <button
                type="button"
                onClick={() => changeQuantity(qty + 1)}
                disabled={selectedStock <= 0 || qty >= selectedStock}
                className="p-3 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="quantity-increase"
              >
                <Plus size={14} />
              </button>
            </div>
            <div className="text-sm text-[#64748B]">
              {!variant && p.variants?.length > 1 ? (
                <span data-testid="stock-variant-selection-required">Select a variant</span>
              ) : selectedStock > 0 && selectedStock <= 10 ? (
                <span className="font-semibold text-amber-700" data-testid="low-stock-indicator">
                  Only {selectedStock} left
                </span>
              ) : selectedStock <= 0 ? (
                <span className="text-red-600" data-testid="out-of-stock-indicator">Out of stock</span>
              ) : null}
            </div>
          </div>

          {selectedStock <= 0 ? <div className="mt-6 border border-[#D9C3A5] bg-[#FDFBF7] p-4" data-testid="notify-me-panel"><p className="font-semibold text-[#1E2A24]">{notifyRequested ? "You’re on the notification list" : "Notify me when available"}</p><div className="mt-3 flex gap-2"><input type="email" value={notifyEmail} onChange={(event) => setNotifyEmail(event.target.value)} placeholder={user?.email || "Email address"} disabled={notifyRequested} className="min-w-0 flex-1 border border-[#E2DCD2] px-3 py-2 disabled:opacity-60" data-testid="notify-me-email" /><button type="button" onClick={subscribeNotifyMe} disabled={notifyRequested} className="bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" data-testid="notify-me-submit">{notifyRequested ? "Request saved" : "Notify me"}</button></div></div> : <div className="mt-6 flex gap-3">
            <button
              onClick={() => {
                addSelectedVariant();
              }}
              className="flex-1 bg-[#2D5A47] hover:bg-[#214435] text-white font-semibold py-3.5 rounded-lg transition active:scale-[0.98]"
              data-testid="add-to-cart"
            >
              Add to Bag
            </button>
            <Link
              to="/checkout"
              onClick={(event) => {
                if (!addSelectedVariant()) event.preventDefault();
              }}
              className="flex-1 text-center bg-[#C86D42] hover:bg-[#AE5931] text-white font-semibold py-3.5 rounded-lg transition"
              data-testid="buy-now"
            >
              Buy Now
            </Link>
            <button
              type="button"
              onClick={handleWishlist}
              className={`border rounded-lg bg-white p-3.5 transition-colors ${
                wishlisted
                  ? "border-red-200 bg-red-50 text-red-500"
                  : "border-[#E2DCD2] text-[#1E2A24] hover:border-[#C86D42]"
              }`}
              data-testid={`pdp-wishlist-toggle-${p.id}`}
              aria-label={wishlisted ? `Remove ${p.name} from wishlist` : `Add ${p.name} to wishlist`}
              aria-pressed={wishlisted}
            >
              <Heart size={18} fill={wishlisted ? "currentColor" : "none"} />
            </button>
          </div>}
          </>}

          <section
            className="mt-7 border-t border-[#E2DCD2] pt-5"
            data-testid="product-share-section"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-[#1E2A24]">Share</p>
                <p className="mt-1 text-xs text-[#64748B]">Send this find to someone special.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={shareOnWhatsApp}
                  className={[
                    "inline-flex items-center gap-2 border border-[#E2DCD2] bg-white px-3 py-2",
                    "text-xs font-semibold text-[#1E2A24] transition-colors hover:bg-[#EBF2EE]",
                  ].join(" ")}
                  data-testid="product-share-whatsapp"
                >
                  <MessageCircle size={15} className="text-[#2D5A47]" />
                  WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => copyProductLink("Product link copied")}
                  className={[
                    "inline-flex items-center gap-2 border border-[#E2DCD2] bg-white px-3 py-2",
                    "text-xs font-semibold text-[#1E2A24] transition-colors hover:bg-[#EBF2EE]",
                  ].join(" ")}
                  data-testid="product-share-copy-link"
                >
                  <Copy size={15} className="text-[#2D5A47]" />
                  Copy Link
                </button>
                <button
                  type="button"
                  onClick={() => copyProductLink(
                    "Link copied — paste in your Instagram bio or story",
                  )}
                  className={[
                    "inline-flex items-center gap-2 border border-[#E2DCD2] bg-white px-3 py-2",
                    "text-xs font-semibold text-[#1E2A24] transition-colors hover:bg-[#EBF2EE]",
                  ].join(" ")}
                  data-testid="product-share-instagram"
                >
                  <Instagram size={15} className="text-[#C86D42]" />
                  Instagram
                </button>
              </div>
            </div>
          </section>

          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { i: Truck, id: "shipping", t: "Free ship ₹499+" },
              { i: Headphones, id: "support", t: "360° Customer Support" },
              { i: RefreshCw, id: "returns", t: returnLabel },
              { i: ShieldCheck, id: "payments", t: "Safe payments" },
            ].map((x) => (
              <div
                key={x.id}
                className="flex min-h-14 items-center gap-2 rounded-lg bg-[#F4F7F2] px-3 py-2 text-xs font-medium text-[#40534A]"
                data-testid={`product-assurance-${x.id}`}
              >
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-[#2D5A47]">
                  <x.i size={15} aria-hidden="true" />
                </span>
                <span>{x.t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-16 border-b border-[#E2DCD2] flex gap-2 overflow-x-auto no-scrollbar">
        <Tab active={tab === "desc"} onClick={() => setTab("desc")}>Description</Tab>
      </div>
      <div className="py-8 text-[#1E2A24] leading-relaxed max-w-3xl">
        {tab === "desc" && <p>{p.description}</p>}
        {tab === "reviews" && (
          <div className="space-y-5" data-testid="product-reviews-section">
            <div>
              <h2 className="font-serif text-2xl font-semibold text-[#1E2A24]">Customer Reviews</h2>
              <p className="mt-1 text-sm text-[#64748B]">Verified feedback from Eira customers.</p>
            </div>
            {reviews.length === 0 ? <p className="text-[#64748B]">No reviews yet. Be the first to review.</p> : reviews.map((r) => (
              <article key={r.id} className="border border-[#E2DCD2] bg-white p-5" data-testid={`product-review-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-[#D99B26]" data-testid={`product-review-stars-${r.id}`}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} size={15} fill={star <= r.rating ? "#D99B26" : "none"} strokeWidth={0} />
                    ))}
                  </div>
                  <span className="text-xs text-[#64748B]" data-testid={`product-review-date-${r.id}`}>
                    {formatReviewDate(r.created_at)}
                  </span>
                </div>
                {r.title && <div className="mt-3 text-sm font-semibold">{r.title}</div>}
                {r.comment && <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#4B5563]">{r.comment}</p>}
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
                  <span>— {r.user_name || "Verified customer"}</span>
                  {r.verified_purchase && <span className="bg-[#EBF2EE] px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#2D5A47]" data-testid={`verified-purchase-${r.id}`}>Verified Purchase</span>}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      <ProductExperienceLuxury product={p} />
      <section
        className="mt-16 bg-white px-4 py-16 sm:px-8"
        data-testid="product-reviews-after-faq"
      >
        <div className="mx-auto max-w-4xl">
          <div className="text-center">
            <h2 className="font-serif text-3xl font-bold text-[#254734] sm:text-4xl">
              Customer Feedback &amp; Reviews
            </h2>
            <p className="mt-3 text-sm text-[#64748B]">Verified feedback from Eira customers.</p>
          </div>
          <div className="mt-10 space-y-4" data-testid="product-reviews-section">
            {reviews.length === 0 ? (
              <p className="text-center text-[#64748B]">No reviews yet. Be the first to review.</p>
            ) : reviews.map((review) => (
              <article
                key={review.id}
                className="bg-[#F4F8F4] p-6"
                data-testid={`product-review-${review.id}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div
                    className="flex items-center gap-2 text-[#D99B26]"
                    data-testid={`product-review-stars-${review.id}`}
                  >
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        fill={star <= review.rating ? "#D99B26" : "none"}
                        size={15}
                        strokeWidth={0}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-[#64748B]" data-testid={`product-review-date-${review.id}`}>
                    {formatReviewDate(review.created_at)}
                  </span>
                </div>
                {review.title && <div className="mt-3 text-sm font-semibold">{review.title}</div>}
                {review.comment && <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#4B5563]">{review.comment}</p>}
                <div className="mt-4 text-xs text-[#64748B]">
                  — {review.user_name || "Verified customer"}
                  {review.verified_purchase && <span className="ml-2 font-semibold text-[#254734]">Verified Purchase</span>}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {p.related?.length > 0 && (
        <div className="mt-12">
          <h3 className="font-serif text-2xl font-semibold mb-6">You may also like</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {p.related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}
      {customersAlsoBought.length > 0 && (
        <section className="mt-16 border-t border-[#E2DCD2] pt-10" data-testid="customers-also-bought-section">
          <h2
            className="font-serif text-3xl font-semibold text-[#1E2A24]"
            data-testid="customers-also-bought-heading"
          >
            Customers Also Bought
          </h2>
          <div
            className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6"
            data-testid="customers-also-bought-grid"
          >
            {customersAlsoBought.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}
      <RecentlyViewedProducts className="mt-16 border-t border-[#E2DCD2] pt-10" />
      </div>
    </>
  );
};

export default ProductDetail;
